import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  ReactNode,
  RefObject,
} from "react";
import { createPortal } from "react-dom";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { CSSReset, GlobalStyle } from "@chakra-ui/react";
import { dom } from "@fortawesome/fontawesome-svg-core";

interface Props {
  children: ReactNode;
}

const fontAwesomeCss = dom.css();

/**
 * Ref to the element inside the current CSSIsolation boundary's shadow root,
 * if any. Chakra's Modal/Drawer/Tooltip portal to document.body by default,
 * which is outside the shadow root's isolated style cache — components that
 * portal must read this and pass it as `portalProps.containerRef` so they
 * render (and get styled) inside the same shadow root as their trigger.
 */
export const CSSIsolationContainerContext =
  createContext<RefObject<HTMLElement | null> | null>(null);

export const useCSSIsolationContainer = () =>
  useContext(CSSIsolationContainerContext);

/**
 * CSSIsolation
 *
 * Renders children inside a Shadow DOM, providing a complete CSS isolation
 * boundary. Legacy element-type selectors (e.g. `.uikit-v1 h2`) cannot
 * cross the shadow boundary, so Chakra UI component styles apply cleanly.
 *
 * CSS custom properties (--chakra-*) defined on :root in the main document
 * are still accessible inside the shadow root via CSS inheritance.
 */
export const CSSIsolation = ({ children }: Props) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [portal, setPortal] = useState<{
    root: ShadowRoot;
    cache: ReturnType<typeof createCache>;
  } | null>(null);

  useLayoutEffect(() => {
    if (!hostRef.current || portal) return;
    const shadowRoot = hostRef.current.attachShadow({ mode: "open" });
    const cache = createCache({
      key: "rt-isolated",
      container: shadowRoot as unknown as HTMLElement,
    });
    setPortal({ root: shadowRoot, cache });
  }, []);

  return (
    <div ref={hostRef}>
      {portal &&
        createPortal(
          // The inner div resets inherited properties from the light DOM.
          // Light DOM styles (e.g. `.uikit-v1 div`) override `:host` rules by
          // spec, so the reset must live on an element *inside* the shadow root
          // where light DOM selectors cannot reach it.
          // `all: initial` does not affect CSS custom properties (--chakra-*),
          // so Chakra tokens defined on :root still inherit through.
          // `all: initial` cuts uikit-inherited properties. Properties declared
          // after `all` in the same rule override it, so we restore the Chakra
          // base values using CSS vars that inherit from :root into the shadow root.
          // This same node is also the portal container for Modal/Drawer/Tooltip
          // (see CSSIsolationContainerContext) — without it, Chakra's portals
          // would escape to document.body, outside this isolated style cache.
          <div
            ref={containerRef}
            style={{
              all: "initial",
              display: "block",
              fontFamily: "var(--chakra-fonts-body)",
              fontSize: "var(--chakra-fontSizes-md)",
              color: "var(--chakra-colors-gray-700)",
              lineHeight: "var(--chakra-lineHeights-base)",
            }}
          >
            {/*
              Bootstrap's reboot sets `img, svg { vertical-align: middle }` on the
              light DOM. That rule can't cross the shadow boundary, so without it
              inline icons fall back to the UA default `vertical-align: baseline`
              and sit flush at the top of their line box instead of centered.
            */}
            <style>{"img, svg { vertical-align: middle; }"}</style>
            {/* FontAwesome's and Chakra's stylesheets reach the page through
                document.head, so they stop at the boundary too. */}
            <style>{fontAwesomeCss}</style>
            <CacheProvider value={portal.cache}>
              <CSSReset />
              <GlobalStyle />
              <CSSIsolationContainerContext.Provider value={containerRef}>
                {children}
              </CSSIsolationContainerContext.Provider>
            </CacheProvider>
          </div>,
          portal.root
        )}
    </div>
  );
};
